import { Link } from "react-router-dom";
import MarketingShell from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";
import { Mic, Stethoscope, ClipboardList, MessagesSquare, ArrowRight, ShieldCheck, Sparkles, Languages, FileCheck2, LineChart, Clock, DollarSign, TrendingUp, Smartphone, Pill, AlertTriangle, CalendarPlus, Lock, Server, KeyRound, Eye, PawPrint } from "lucide-react";

const FEATURES = [
  { icon: Mic, title: "Ambient SOAP scribe", body: "Record the exam — Vetaura drafts the S/O/A/P for you to review and sign in seconds." },
  { icon: ClipboardList, title: "Differential checklists", body: "Signalment + history in, transparent differentials, workup and questions out." },
  { icon: MessagesSquare, title: "Discharge & inbox", body: "Plain-language, multilingual pet-owner instructions and empathetic reply drafts." },
  { icon: Stethoscope, title: "Case handoffs", body: "One-click shift summaries with active problems, pending labs and next actions." },
  { icon: LineChart, title: "Longitudinal trends", body: "Watch weight, renal values, glucose and pain scores drift across every visit." },
  { icon: Languages, title: "Multilingual owners", body: "Send discharges in Spanish, French, German, Mandarin and Portuguese." },
  { icon: FileCheck2, title: "Audit-ready", body: "Every AI draft and signed note logged with clinician, timestamp and content." },
  { icon: ShieldCheck, title: "Vet-in-the-loop", body: "AI drafts only. Nothing enters the record until a clinician reviews and signs." },
];

export default function Landing() {
  return (
    <MarketingShell>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> 14-day free trial · no card required
          </div>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-emerald-950 leading-[1.05]">
            The clinic copilot that makes sure nothing falls through the cracks.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            Vetaura classifies every client message, drafts staff-reviewed replies, auto-generates every post-op and follow-up task, and surfaces the patients overdue for vaccines, dental and monitoring — plus an ambient SOAP scribe and multilingual owner discharges. All vet-in-the-loop.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/try-it" data-testid="landing-cta-try-it">
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 h-11">
                Try it on your clinic <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/signup" data-testid="landing-cta-signup">
              <Button variant="outline" className="rounded-full px-6 h-11 border-emerald-200 hover:bg-emerald-50 text-emerald-900">Start free trial</Button>
            </Link>
            <Link to="/demo" data-testid="landing-cta-demo">
              <Button variant="ghost" className="rounded-full px-6 h-11 text-emerald-900 hover:bg-emerald-50">Book a live demo</Button>
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
            <div><span className="font-mono-clinical text-emerald-800 text-base font-semibold">7 buckets</span><br />message classification</div>
            <div className="h-8 w-px bg-emerald-100" />
            <div><span className="font-mono-clinical text-emerald-800 text-base font-semibold">Auto tasks</span><br />post-op, monitoring, gaps</div>
            <div className="h-8 w-px bg-emerald-100" />
            <div><span className="font-mono-clinical text-emerald-800 text-base font-semibold">6 languages</span><br />owner discharge portal</div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-3 rounded-3xl bg-emerald-100/40 blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden border border-emerald-100 shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1631980546596-a70e0d4c7cf7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHx2ZXRlcmluYXJpYW4lMjBleGFtaW5pbmclMjBnb2xkZW4lMjByZXRyaWV2ZXIlMjBkb2clMjBjbGluaWN8ZW58MHx8fHwxNzg4MTM0MTMyfDA&ixlib=rb-4.1.0&q=85"
              alt="Golden retriever with owner"
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute left-4 right-4 bottom-4 rounded-2xl bg-white/95 backdrop-blur border border-emerald-100 p-4 shadow-lg">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-rose-500 animate-pulse" /> Ambient recording · 04:12
              </div>
              <div className="mt-2 text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold text-emerald-900">S:</span> Bella, 7yo SF Golden Retriever, presented for recheck of chronic otitis externa AU. Owner reports increased head shaking over the last 3 days…
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Every appointment</div>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-semibold text-emerald-950 tracking-tight">Built for the tedious parts, so you stay with the patient.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-md transition-all">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center text-emerald-700 mb-4">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="font-semibold text-slate-900 mb-1">{f.title}</div>
              <div className="text-sm text-slate-600 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Compliance strip */}
      <section className="bg-emerald-900 text-emerald-50">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-semibold">Safe by design</div>
            <h3 className="mt-2 font-serif-display text-2xl font-semibold">Every output reviewed by a clinician.</h3>
            <p className="text-sm text-emerald-100/80 mt-3">Vetaura writes drafts. You sign what enters the record. Full audit log, per-user signatures, and clinic-branded exports.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-semibold">Owner-friendly</div>
            <h3 className="mt-2 font-serif-display text-2xl font-semibold">Discharges owners actually read.</h3>
            <p className="text-sm text-emerald-100/80 mt-3">Share a mobile-friendly page with meds, warning signs and a one-tap recheck booking — translated into the owner's language.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-semibold">Practice fit</div>
            <h3 className="mt-2 font-serif-display text-2xl font-semibold">Solo to multi-site.</h3>
            <p className="text-sm text-emerald-100/80 mt-3">Plans that scale with your caseload — with clinic-level branding, staff seats and shift handoffs.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-16 flex flex-wrap gap-3">
          <Link to="/pricing"><Button className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-full px-6 h-11" data-testid="landing-view-pricing">View pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          <Link to="/signup"><Button variant="outline" className="rounded-full px-6 h-11 border-white/30 text-white bg-white/0 hover:bg-white/10" data-testid="landing-start-trial-bottom">Start free trial</Button></Link>
        </div>
      </section>

      {/* ROI numbers */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">The numbers</div>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-semibold text-emerald-950 tracking-tight">Time back. Money back. Better care.</h2>
          <p className="mt-3 text-slate-600">A 3-DVM Growth-plan clinic recovers roughly $16,000 in reclaimed clinical hours in month one — for less than the cost of an extra front-desk hire.</p>
        </div>

        <div className="mt-10 grid md:grid-cols-4 gap-4">
          {[
            { icon: Clock, big: "~2 hr / day", label: "documentation reclaimed per DVM" },
            { icon: DollarSign, big: "$5,600 / mo", label: "value recovered per DVM (est.)" },
            { icon: TrendingUp, big: "+15–25%", label: "recheck & follow-through" },
            { icon: ShieldCheck, big: "≤ 5 min", label: "exam to signed SOAP" },
          ].map((s) => (
            <div key={s.big} className="p-5 rounded-2xl border border-emerald-100 bg-white shadow-sm" data-testid={`landing-roi-${s.big.replace(/\W+/g,'').toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div className="font-serif-display text-3xl font-semibold text-emerald-950">{s.big}</div>
                <div className="h-8 w-8 rounded-lg bg-emerald-50 border border-emerald-100 grid place-items-center text-emerald-700"><s.icon className="h-4 w-4" /></div>
              </div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mt-2">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/roi" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-900" data-testid="landing-see-roi">Open the ROI calculator <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Owner walkthrough */}
      <section className="bg-emerald-50/50 border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
              <PawPrint className="h-3.5 w-3.5" /> For your pet parents
            </div>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl font-semibold text-emerald-950 tracking-tight leading-tight">How pet parents access their pet's plan — in 3 taps.</h2>
            <div className="mt-6 space-y-4">
              {[
                { n: 1, icon: Smartphone, t: "Receive a private link", d: "The clinic texts or emails a clinic-branded link. No app. No password." },
                { n: 2, icon: Pill, t: "See meds, warnings, recheck", d: "Everything in their language, in plain words, mobile-first." },
                { n: 3, icon: CalendarPlus, t: "One-tap recheck booking", d: "The clinic gets a booking request; the appointment lands on your schedule." },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-emerald-700 text-white grid place-items-center font-mono-clinical text-xs flex-shrink-0">{s.n}</div>
                  <div>
                    <div className="font-semibold text-emerald-950 flex items-center gap-2"><s.icon className="h-4 w-4 text-emerald-700" /> {s.t}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/how-it-works" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-900" data-testid="landing-owner-walkthrough">See the full owner walkthrough <ArrowRight className="h-4 w-4" /></Link>
          </div>

          {/* Phone mock */}
          <div className="flex justify-center">
            <div className="w-[300px] rounded-[36px] bg-slate-900 p-3 shadow-2xl">
              <div className="rounded-[28px] overflow-hidden bg-[#f8f9f6]">
                <div className="bg-emerald-800 text-white px-5 py-5">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-xl bg-white/15 grid place-items-center"><PawPrint className="h-5 w-5" /></div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-white/70 font-semibold">Discharge summary</div>
                      <div className="font-serif-display text-base font-semibold">Meadow Ridge Vet</div>
                      <div className="text-[10px] text-white/80">For Bella · Signed by Dr. Reyes</div>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div className="text-xs text-slate-700 leading-relaxed">Hi Amelia — Bella had a great visit today. Her ear infection is looking a lot better…</div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                    <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1"><Pill className="h-3 w-3" /> Medications</div>
                    <div className="mt-1 text-xs font-semibold text-emerald-900">Mometamax drops</div>
                    <div className="text-[10px] font-mono-clinical text-slate-600">1 drop each ear · once daily · 10 days</div>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                    <div className="text-[9px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Call immediately if…</div>
                    <div className="text-[10px] text-amber-900 mt-1">Bella becomes lethargic, stops eating, or the ear swells.</div>
                  </div>
                  <button className="w-full h-9 rounded-full bg-emerald-800 text-white text-xs font-semibold flex items-center justify-center gap-1"><CalendarPlus className="h-3 w-3" /> Request recheck appointment</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security promise */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Safe, private, yours</div>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-semibold text-emerald-950 tracking-tight">Your clinic's data. In your clinic's control.</h2>
          <p className="mt-3 text-slate-600">Every clinic gets an isolated tenant. Data encrypted end-to-end, siloed by clinic_id, never used to train AI, exportable at any time, deletable on request.</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Lock, t: "AES-256 at rest · TLS 1.3", d: "Encrypted everywhere, always." },
            { icon: Server, t: "Tenant isolation", d: "Query-layer scoping. Never bleeds between clinics." },
            { icon: KeyRound, t: "Your MFA · Your policy", d: "Google Workspace SSO. Instant revocation." },
            { icon: Eye, t: "No training on your data", d: "Zero-retention model APIs. Not for sale, not for training." },
          ].map((f) => (
            <div key={f.t} className="p-5 rounded-2xl border border-emerald-100 bg-white shadow-sm" data-testid={`landing-security-${f.t.split(" ")[0].toLowerCase()}`}>
              <div className="h-9 w-9 rounded-lg bg-emerald-50 border border-emerald-100 grid place-items-center text-emerald-700 mb-2"><f.icon className="h-4 w-4" /></div>
              <div className="font-semibold text-emerald-950 text-sm">{f.t}</div>
              <div className="text-xs text-slate-600 mt-1">{f.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/security" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-900" data-testid="landing-security-link">See the full security & legal breakdown <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-emerald-900 text-white p-10 sm:p-16 text-center">
          <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-semibold">Turn-key. Vet-in-the-loop. Ready today.</div>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">The AI copilot every veterinary practice will run in 2026.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/signup"><Button className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-full h-11 px-6" data-testid="landing-final-signup">Start your free trial <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/demo"><Button variant="outline" className="rounded-full h-11 px-6 border-white/30 text-white bg-white/0 hover:bg-white/10" data-testid="landing-final-demo">Book a live demo</Button></Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
